<template>
  <div class="tc-app-shell">
    <AppHeader />

    <div class="tc-verify-shell" data-testid="verify-screen">
      <!-- Success state: replaces the list once the write lands, then auto-navigates
           home preserving the ?saved= flash. Failure path keeps the list + banner. -->
      <div
        v-if="saved"
        class="tc-verify-success"
        data-testid="verify-success"
        role="status"
        aria-live="polite"
      >
        <div class="tc-verify-success-check" aria-hidden="true">
          <svg viewBox="0 0 52 52" class="tc-verify-success-check-svg">
            <circle class="tc-verify-success-check-circle" cx="26" cy="26" r="24" fill="none" />
            <path class="tc-verify-success-check-mark" fill="none" d="M14 27 L22 35 L38 18" />
          </svg>
        </div>
        <h2 class="tc-verify-success-title">Published to Vantagepoint</h2>
        <div class="tc-verify-success-detail" data-testid="verify-success-detail">
          {{ saved.hours.toFixed(2) }}h across {{ saved.lines }}
          {{ saved.lines === 1 ? 'line' : 'lines' }}
        </div>
        <div
          v-if="reconciling"
          class="tc-verify-success-reconcile"
          data-testid="verify-reconciling"
        >
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <span>Updating timesheet</span>
        </div>
      </div>

      <template v-else>
        <div class="tc-verify-scroll tc-scroll">
          <div class="tc-verify-container">
            <button
              type="button"
              class="tc-verify-back"
              data-testid="verify-back"
              @click="goBack"
            >‹ Back to Triage</button>

            <div class="tc-verify-header">
              <div>
                <h1 class="tc-verify-title">Verify &amp; Publish</h1>
                <div class="tc-verify-subtitle">
                  <template v-if="periodLabel">{{ periodLabel }} · </template>
                  <span data-testid="verify-entry-count">{{ confirmedDrafts.length }}</span>
                  {{ confirmedDrafts.length === 1 ? 'entry' : 'entries' }} ·
                  review before publishing to Vantagepoint
                </div>
              </div>
              <div v-if="confirmedDrafts.length > 0" class="tc-verify-total">
                <div class="tc-verify-total-num" data-testid="verify-total-hours">{{ totalHours.toFixed(2) }}h</div>
                <div
                  class="tc-verify-total-target"
                  :class="meetsTarget ? 'tc-verify-total-target--met' : 'tc-verify-total-target--under'"
                  data-testid="verify-target-line"
                >{{ targetLine }}</div>
              </div>
            </div>

            <!-- Already-in-VP info banner: brand tint, info left border. Hidden
                 when VP is empty for the period. -->
            <div
              v-if="vpHoursTotal > 0"
              class="tc-verify-vp-notice"
              data-testid="verify-vp-notice"
              role="note"
            >
              <span class="tc-verify-vp-notice-icon" aria-hidden="true">i</span>
              <span>
                <strong>{{ vpHoursTotal.toFixed(2) }}h</strong>
                already in your timesheet won't be changed — only new lines below will be written.
              </span>
            </div>

            <div
              v-if="banner"
              :class="['tc-verify-flash', `tc-verify-flash--${banner.kind}`]"
              role="alert"
              data-testid="verify-banner"
            >
              <div>{{ banner.message }}</div>
              <div v-if="banner.kind === 'warn'" class="tc-verify-flash-actions">
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm"
                  data-testid="verify-refresh-retry"
                  @click="onRefreshAndRetry"
                >Refresh and retry</button>
              </div>
            </div>

            <!-- VP-read gate — without the period's VP rows every entry would read as
                 new and be written again -->
            <div
              v-if="vpReadFailed"
              class="tc-verify-flash tc-verify-flash--error"
              role="alert"
              data-testid="verify-vp-read-error"
            >Can’t read your Vantagepoint timesheet, so publishing is paused. Reload to try again.</div>

            <!-- Phase-needed gate — blocks submit until every phased row has a leaf -->
            <div
              v-if="phasedNeedingLeafCount > 0"
              class="tc-verify-flash tc-verify-flash--warn"
              role="alert"
              data-testid="verify-phase-needed-banner"
            >{{ leafNeededMessage(leafNeeded) }} Go back and tap each flagged row to pick one.</div>

            <!-- Note-needed gate — VP requires a note on every entry for this tenant -->
            <div
              v-if="entriesNeedingComment > 0"
              class="tc-verify-flash tc-verify-flash--warn"
              role="alert"
              data-testid="verify-comment-needed-banner"
            >{{ entriesNeedingComment }} {{ entriesNeedingComment === 1 ? 'entry needs' : 'entries need' }} a note before you can publish. Vantagepoint requires a note on every entry — add one on each flagged row.</div>

            <!-- Out-of-period gate — the save API rejects the whole period on
                 the first bad entry, so this is caught before the request -->
            <div
              v-if="outOfPeriodCount > 0"
              class="tc-verify-flash tc-verify-flash--warn"
              role="alert"
              data-testid="verify-out-of-period-banner"
            >{{ outOfPeriodCount }} {{ outOfPeriodCount === 1 ? 'entry falls' : 'entries fall' }} outside {{ periodLabel }}. Remove {{ outOfPeriodCount === 1 ? 'it' : 'them' }} on the flagged {{ outOfPeriodCount === 1 ? 'row' : 'rows' }} to publish the rest.</div>

            <template v-if="confirmedDrafts.length === 0">
              <div class="tc-verify-empty" data-testid="verify-empty">
                <p class="tc-verify-empty-title">No entries to verify</p>
                <p>Confirm some events on the Triage screen before submitting.</p>
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  data-testid="verify-empty-back"
                  @click="goBack"
                >Back to Triage</button>
              </div>
            </template>

            <template v-else>
              <VerifyDayStrip
                :period-start="periodStart"
                :period-end="periodEnd"
                :drafts="combinedDayItems"
              />

              <div
                v-if="hasAnyConflict"
                class="tc-verify-flash tc-verify-flash--warn"
                role="status"
              >
                Some entries will add to hours already in Vantagepoint. Both rows will be written — VP will sum them. Review the highlighted rows before publishing.
              </div>

              <div class="tc-verify-day-groups">
                <VerifyDayGroup
                  v-for="group in dayGroups"
                  :key="group.dayIndex"
                  :group="group"
                  :period-start="periodStart"
                  :row-errors="rowErrors"
                  :comment-required="commentRequired"
                  :hours-increment="hoursIncrement"
                  :show-labor-code="showLaborCode"
                  :can-edit-labor-code="canEditLaborCode"
                  :labor-codes="laborCodes"
                  :default-labor-code="defaultLaborCode"
                  :cell-vp-status="cellVpStatus"
                  :conflict-vp-hours="conflictVpHours"
                  @update="onRowUpdate"
                  @remove="onRowRemove"
                />
              </div>
            </template>
          </div>
        </div>

        <div
          v-if="confirmedDrafts.length > 0"
          class="tc-verify-submit-bar"
          data-testid="verify-submit-bar"
        >
          <span class="tc-verify-submit-summary">
            {{ confirmedDrafts.length }} new {{ confirmedDrafts.length === 1 ? 'line' : 'lines' }} ·
            {{ totalHours.toFixed(2) }}h
          </span>
          <button
            type="button"
            class="btn btn-outline-secondary"
            data-testid="verify-cancel"
            :disabled="submitting"
            @click="goBack"
          >Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            data-testid="verify-submit"
            :disabled="!canSubmit"
            @click="onSubmit"
          >
            <span v-if="submitting">Publishing…</span>
            <span v-else>Publish to Vantagepoint</span>
          </button>
          <button
            type="button"
            class="btn tc-verify-submit-and-approve"
            data-testid="verify-submit-and-approve"
            disabled
            title="Submit for manager approval — coming in next sprint"
          >Write and Submit</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import AppHeader from '../components/AppHeader.vue';
import VerifyDayStrip from '../components/VerifyDayStrip.vue';
import VerifyDayGroup, { type DayGroup } from '../components/VerifyDayGroup.vue';
import type { VpDayEntry } from '../components/VerifyVpRow.vue';
import { useTriageStore } from '../stores/triage';
import type { TriageDraft } from '../stores/triage';
import { ApiClientError, getCurrentTimesheet, type VpRejection } from '../api/client';
import type { TimesheetResponse, TimesheetRow } from '../api/client';
import { getDraft } from '../api/draft';
import type { DraftResponse } from '../api/draft';
import { getTimesheetSettings } from '../api/timesheetSettings';
import type { TimesheetSettings } from '../api/timesheetSettings';
import { reconcileDraft, applyVpWbsRequirements, rowsNeedingLeaf, leafNeededMessage } from '../utils/reconcileDraft';
import { useVpWbsRequirements } from '../composables/useVpWbsRequirements';
import { formatDateRange, formatMonthDay, periodDayCount } from '../utils/period';
import { useTimezoneStore } from '../stores/timezone';
import { saveTimesheet } from '../api/timesheet';
import type { SaveTimesheetEntry, SaveTimesheetRequest } from '../api/timesheet';
import { useTimesheetSettings } from '../composables/useTimesheetSettings';

const router = useRouter();
const queryClient = useQueryClient();
const triageStore = useTriageStore();
const tzStore = useTimezoneStore();

// Selected period — from the triage store (persisted across reloads in
// sessionStorage). Without a period there is nothing to verify.
const periodStart = computed(() => triageStore.activePeriodStart ?? '');
const periodEnd = computed(() => triageStore.activePeriodEnd ?? '');

// VP rows for the selected period, keyed by it so the query shares the triage
// panel's period-scoped cache (Bug 5954). Disabled until a period is known.
const tsQuery = useQuery<TimesheetResponse, ApiClientError>({
  queryKey: computed(() => ['timesheet', periodEnd.value]),
  queryFn: () => getCurrentTimesheet(periodEnd.value),
  enabled: computed(() => !!periodEnd.value),
  retry: false,
  staleTime: 30_000,
});

// The draft itself — same query key as the triage panel, so this is served
// from the Vue Query cache (already fetched on /home) rather than re-run.
// Reconciled with the edit overlay so picks are reflected, then flattened to
// one submittable entry per (target, day) cell.
const draftQuery = useQuery<DraftResponse, ApiClientError>({
  queryKey: computed(() => ['draft', periodStart.value, periodEnd.value, tzStore.selectedZone]),
  queryFn: () => getDraft(periodStart.value || undefined, periodEnd.value || undefined, tzStore.selectedZone || undefined),
  enabled: computed(() => !!periodStart.value),
  retry: false,
  staleTime: 60_000,
});

// Tenant timesheet settings — drives whether a note is mandatory on every
// entry. VP enforces this server-side ("Comment required for hours entered"),
// so when it's on we block submit until each entry carries a note. Cached and
// shared with the triage panel's settings query.
const settingsQuery = useQuery<TimesheetSettings, ApiClientError>({
  queryKey: ['timesheet-settings'],
  queryFn: getTimesheetSettings,
  retry: false,
  staleTime: 300_000,
});

// VP encodes RequireComments as Y / N / C (and " " when unset). Anything that
// isn't an explicit "N"/unset requires a note — "C" (conditional) rejected
// noteless entries live (Bug 5956 follow-up). NB: this is the tenant default;
// per-project RequireComments is the precise signal (tracked separately).
const commentRequired = computed(() => {
  const v = settingsQuery.data.value?.requireComments?.trim();
  return !!v && v !== 'N';
});

// Tenant-level settings surfaced to VerifyRow/VerifyDayGroup. The settings query
// is shared with the triage panel (same query key) so this is always a cache hit.
const tsSettings = useTimesheetSettings();
const hoursIncrement = tsSettings.hoursIncrement;
const showLaborCode = tsSettings.showLaborCode;
const canEditLaborCode = tsSettings.canEditLaborCode;
const laborCodes = tsSettings.laborCodes;
const defaultLaborCode = tsSettings.defaultLaborCode;

const reconciledDraft = computed((): DraftResponse | null =>
  draftQuery.data.value ? reconcileDraft(draftQuery.data.value, [...triageStore.drafts.values()]) : null,
);
const vpRequirements = useVpWbsRequirements(reconciledDraft);
const effectiveDraft = computed((): DraftResponse | null =>
  reconciledDraft.value ? applyVpWbsRequirements(reconciledDraft.value, vpRequirements.value) : null,
);

function cellId(wbs1: string, wbs2: string, wbs3: string, dayIndex: number): string {
  return `${wbs1}|${wbs2}|${wbs3}|${dayIndex}`;
}

// Every (target, day) cell of the reconciled draft, shaped like a TriageDraft
// so VerifyRow renders it. Opt-out: all cells are submittable by default; the
// user removes the ones they don't want.
const draftCells = computed((): TriageDraft[] => {
  const ed = effectiveDraft.value;
  if (!ed) return [];
  const cells: TriageDraft[] = [];
  for (const row of ed.rows) {
    for (const day of row.days) {
      cells.push({
        eventId: cellId(row.target.wbs1, row.target.wbs2, row.target.wbs3, day.dayIndex),
        status: 'confirmed',
        wbs1: row.target.wbs1,
        wbs1Name: row.target.wbs1Name,
        wbs2: row.target.wbs2,
        wbs3: row.target.wbs3,
        ...(row.target.category ? { category: row.target.category } : {}),
        hours: day.hours,
        dayIndex: day.dayIndex,
        // The day's note is the work description → saved as the VP entry comment.
        ...(day.note.text ? { comment: day.note.text } : {}),
      });
    }
  }
  return cells;
});

// Per-cell signature hashes — echoed back on save so each confirmed pick is
// recorded against the right signature in memory (ADR-0027).
const cellSignatures = computed((): Map<string, string[]> => {
  const m = new Map<string, string[]>();
  const ed = effectiveDraft.value;
  if (!ed) return m;
  for (const row of ed.rows) {
    for (const day of row.days) {
      const id = cellId(row.target.wbs1, row.target.wbs2, row.target.wbs3, day.dayIndex);
      m.set(id, [...new Set(row.contributingEvents
        .filter(c => c.dayIndex === day.dayIndex)
        .map(c => c.signatureHash)
        .filter(Boolean))]);
    }
  }
  return m;
});

// Source events behind each cell, echoed on save so the server can mark them
// submitted. The calendar events outlive the save, so the engine keeps
// proposing them; only this record says they are done (#6240).
const cellEventIds = computed((): Map<string, string[]> => {
  const m = new Map<string, string[]>();
  const ed = effectiveDraft.value;
  if (!ed) return m;
  for (const row of ed.rows) {
    for (const day of row.days) {
      const id = cellId(row.target.wbs1, row.target.wbs2, row.target.wbs3, day.dayIndex);
      m.set(id, [...new Set(row.contributingEvents
        .filter(c => c.dayIndex === day.dayIndex)
        .map(c => c.eventId)
        .filter(Boolean))]);
    }
  }
  return m;
});

// Last-mile review state for this submission (ephemeral): cells the user
// dropped (partial submit) and inline hours/comment tweaks.
const removedCells = ref<Set<string>>(new Set());
const hourEdits = ref<Map<string, number>>(new Map());
const commentEdits = ref<Map<string, string>>(new Map());
const laborCodeEdits = ref<Map<string, string>>(new Map());

const periodLabel = computed(() => {
  if (!periodStart.value || !periodEnd.value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(periodEnd.value);
  const year = m ? m[1] : '';
  return `${formatDateRange(periodStart.value, periodEnd.value)}, ${year}`;
});

const confirmedDrafts = computed((): TriageDraft[] =>
  draftCells.value
    .filter((c) => !removedCells.value.has(c.eventId))
    .map((c) => ({
      ...c,
      hours: hourEdits.value.get(c.eventId) ?? c.hours,
      comment: commentEdits.value.get(c.eventId) ?? c.comment,
      laborCode: laborCodeEdits.value.has(c.eventId)
        ? (laborCodeEdits.value.get(c.eventId) || undefined)
        : (c.laborCode || defaultLaborCode.value || undefined),
    }))
    .filter((c) => c.wbs1 && (c.hours ?? 0) > 0)
    // Already in VP at the exact tuple + hours → the VP row covers it; don't
    // re-write it (no double-booking on the partial-then-return flow).
    .filter((c) => cellVpStatus(c) !== 'in-vp'),
);

const sortedDrafts = computed((): TriageDraft[] => {
  return [...confirmedDrafts.value].sort((a, b) => {
    const ai = a.dayIndex ?? 0;
    const bi = b.dayIndex ?? 0;
    if (ai !== bi) return ai - bi;
    return (a.wbs1 ?? '').localeCompare(b.wbs1 ?? '');
  });
});

const totalHours = computed(() =>
  confirmedDrafts.value.reduce((sum, d) => sum + (d.hours ?? 0), 0),
);

// Expected hours for the period = weekday count × standard 8h day, computed
// the same way the design north star does (other-screens.jsx VerifyScreen:
// `periodDays.filter(!isWeekend).length * 8`). The triage panel uses a flat
// targetHours=80; that ignores holidays/short periods, so Verify derives its
// own weekday-based expected value here.
const STANDARD_DAY_HOURS = 8;
const expectedHours = computed((): number => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(periodStart.value);
  if (!m) return 0;
  const base = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const dayCount = periodDayCount(periodStart.value, periodEnd.value);
  let weekdays = 0;
  for (let i = 0; i < dayCount; i++) {
    const d = new Date(base);
    d.setUTCDate(d.getUTCDate() + i);
    const dow = d.getUTCDay();
    if (dow !== 0 && dow !== 6) weekdays++;
  }
  return weekdays * STANDARD_DAY_HOURS;
});

const meetsTarget = computed(() => totalHours.value >= expectedHours.value);

const targetLine = computed((): string =>
  meetsTarget.value
    ? `meets ${expectedHours.value}h target`
    : `${(expectedHours.value - totalHours.value).toFixed(2)}h under target`,
);

// ---------------------------------------------------------------------------
// VP rows — flattened to one entry per (wbs1, dayIndex) cell with hours > 0
// ---------------------------------------------------------------------------
const vpRows = computed((): TimesheetRow[] => tsQuery.data.value?.rows ?? []);

const vpDayEntries = computed((): VpDayEntry[] => {
  const result: VpDayEntry[] = [];
  for (const row of vpRows.value) {
    for (const cell of row.days) {
      if (cell.total > 0) {
        result.push({
          wbs1: row.wbs1,
          wbs1Name: row.wbs1Name || '(unnamed)',
          dayIndex: cell.index - 1, // backend is 1-based
          hours: cell.total,
        });
      }
    }
  }
  return result;
});

const vpHoursTotal = computed(() =>
  vpDayEntries.value.reduce((s, e) => s + e.hours, 0),
);

// ---------------------------------------------------------------------------
// Per-cell reconcile against live VP (B.7) under the always-append model: TC
// only ever adds rows, so a draft cell is either already covered by a VP row
// (skip it) or it will be appended and VP will sum it onto what's there.
// ---------------------------------------------------------------------------
const tupleKey = (wbs1: string, wbs2: string, wbs3: string, dayIndex: number) =>
  `${wbs1}|${wbs2}|${wbs3}|${dayIndex}`;

// Every VP cell-hours value at each (tuple + day), one entry per VP row. VP keys
// rows by Seq and allows duplicate tuples, so duplicate-tuple rows each contribute
// — the exact-match dedup below must see them all, not just the last one.
const vpCellHoursByTuple = computed((): Map<string, number[]> => {
  const m = new Map<string, number[]>();
  for (const row of vpRows.value) {
    for (const cell of row.days) {
      if (cell.total > 0) {
        const k = tupleKey(row.wbs1, row.wbs2, row.wbs3, cell.index - 1);
        const arr = m.get(k);
        if (arr) arr.push(cell.total);
        else m.set(k, [cell.total]);
      }
    }
  }
  return m;
});

// Total VP hours at each (wbs1, day) — used both for the overlap check and to
// surface the existing-hours figure on the conflict chip ("Adds to N.Nh in VP").
const vpHoursByWbs1Day = computed((): Map<string, number> => {
  const m = new Map<string, number>();
  for (const e of vpDayEntries.value) {
    const k = `${e.wbs1}:${e.dayIndex}`;
    m.set(k, (m.get(k) ?? 0) + e.hours);
  }
  return m;
});

type VpStatus = 'new' | 'in-vp' | 'overlap';

function cellVpStatus(cell: TriageDraft): VpStatus {
  const day = cell.dayIndex ?? 0;
  const vpHoursAtCell = vpCellHoursByTuple.value.get(tupleKey(cell.wbs1 ?? '', cell.wbs2 ?? '', cell.wbs3 ?? '', day));
  // A VP row already carries this exact tuple + day + hours → covered; re-appending
  // would double it, so drop it. This is the re-save idempotency guard.
  if (vpHoursAtCell?.includes(cell.hours ?? 0)) return 'in-vp';
  // Same project + day already has VP hours → the appended row adds on top; VP sums.
  if (vpHoursByWbs1Day.value.has(`${cell.wbs1}:${day}`)) return 'overlap';
  return 'new';
}

// Existing VP hours at an overlapping cell's (project, day) — the figure shown
// on the conflict chip. Already computed for the overlap check; surfaced here.
function conflictVpHours(cell: TriageDraft): number {
  return vpHoursByWbs1Day.value.get(`${cell.wbs1}:${cell.dayIndex ?? 0}`) ?? 0;
}

// Combined items for VerifyDayStrip (VP + drafts show totals together)
const combinedDayItems = computed(() => [
  ...vpDayEntries.value.map(e => ({ dayIndex: e.dayIndex, hours: e.hours })),
  ...confirmedDrafts.value.map(d => ({ dayIndex: d.dayIndex, hours: d.hours })),
]);

// Conflict = an 'overlap' cell: the appended row lands on a (project, day) that
// already has VP hours, so both rows are written and VP sums them. (A cell that
// exactly matches an existing tuple + day + hours is deduped away as 'in-vp' and
// never reaches here.)
const conflictSet = computed((): Set<string> => {
  const s = new Set<string>();
  for (const d of confirmedDrafts.value) {
    if (cellVpStatus(d) === 'overlap') s.add(`${d.wbs1}:${d.dayIndex ?? 0}`);
  }
  return s;
});

function isConflict(wbs1: string, dayIndex: number): boolean {
  return conflictSet.value.has(`${wbs1}:${dayIndex}`);
}

const hasAnyConflict = computed(() => conflictSet.value.size > 0);

// Day-grouped unified view: each group contains VP rows and draft rows for that day
const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function dayLabel(dayIndex: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(periodStart.value);
  if (!m) return `Day ${dayIndex + 1}`;
  const base = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  base.setUTCDate(base.getUTCDate() + dayIndex);
  return `${DOW_SHORT[base.getUTCDay()]} ${formatMonthDay(base.toISOString().slice(0, 10))}`;
}

const dayGroups = computed((): DayGroup[] => {
  const daySet = new Set<number>();
  for (const e of vpDayEntries.value) daySet.add(e.dayIndex);
  for (const d of confirmedDrafts.value) daySet.add(d.dayIndex ?? 0);

  return Array.from(daySet)
    .sort((a, b) => a - b)
    .map(di => {
      const vp = vpDayEntries.value.filter(e => e.dayIndex === di);
      const drafts = sortedDrafts.value.filter(d => (d.dayIndex ?? 0) === di);
      return {
        dayIndex: di,
        label: dayLabel(di),
        totalHours: vp.reduce((s, e) => s + e.hours, 0) + drafts.reduce((s, d) => s + (d.hours ?? 0), 0),
        vpRows: vp,
        draftRows: drafts,
        hasConflict: drafts.some(d => isConflict(d.wbs1 ?? '', di)),
      };
    });
});

interface Banner {
  kind: 'error' | 'warn' | 'success';
  message: string;
}

const banner = ref<Banner | null>(null);
const submitting = ref(false);
const serverRowErrors = ref<Record<string, string>>({});

// Cells the save API would reject, found before the request is built. It fails
// the whole period on the first bad entry, so one unsendable row costs every
// other row too — and the reviewer met that as four identical rejected POSTs
// and a contract string to decode (#6236).
const preflightErrors = computed((): Record<string, string> => {
  const days = periodDayCount(periodStart.value, periodEnd.value);
  if (days === 0) return {};
  const errors: Record<string, string> = {};
  for (const d of confirmedDrafts.value) {
    const day = d.dayIndex ?? 0;
    if (day < 0 || day >= days) errors[d.eventId] = 'Outside this period — remove it to save the rest.';
  }
  return errors;
});

const outOfPeriodCount = computed(() => Object.keys(preflightErrors.value).length);

// The row's own message wins: the server named it, so it is the later word.
const rowErrors = computed((): Record<string, string> =>
  ({ ...preflightErrors.value, ...serverRowErrors.value }),
);

// In-page success state — set when the write lands, then auto-navigates home.
const saved = ref<{ hours: number; lines: number } | null>(null);
// True while the post-save refetch is in flight, so the success state says what
// it is waiting for rather than sitting still (#6546).
const reconciling = ref(false);
// The success state's own beat. The reconcile usually lands well inside it.
const SUCCESS_DWELL_MS = 1500;

// Draft rows missing a level VP requires (a phase, or a task under it). VP
// rejects a charge above the leaf, so Verify & Save is blocked until each is picked.
const leafNeeded = computed(() => rowsNeedingLeaf(effectiveDraft.value));
const phasedNeedingLeafCount = computed((): number => leafNeeded.value.phase + leafNeeded.value.task);

// Entries missing a note when VP requires one. VP rejects a noteless entry
// ("Comment required for hours entered"), so we gate submit on these instead of
// letting the save round-trip fail. A note added inline (VerifyRow) clears it.
function needsComment(cell: TriageDraft): boolean {
  return commentRequired.value && !(cell.comment ?? '').trim();
}

const entriesNeedingComment = computed((): number =>
  confirmedDrafts.value.filter(needsComment).length,
);

// Publish only against a settled VP read: "already in Vantagepoint" is decided
// from it, and a failed publish may still have written (#7468).
const vpReadSettled = computed(() => tsQuery.isSuccess.value && !tsQuery.isFetching.value);
const vpReadFailed = computed(() => tsQuery.isError.value);

const canSubmit = computed(
  () => !submitting.value
    && vpReadSettled.value
    && confirmedDrafts.value.length > 0
    && !!periodEnd.value
    && phasedNeedingLeafCount.value === 0
    && entriesNeedingComment.value === 0
    && outOfPeriodCount.value === 0,
);

function goBack() {
  void router.push('/home');
}

function clearRowError(cellKey: string) {
  if (serverRowErrors.value[cellKey]) {
    const next = { ...serverRowErrors.value };
    delete next[cellKey];
    serverRowErrors.value = next;
  }
}

function onRowUpdate(cellKey: string, patch: { hours: number; comment: string; laborCode?: string }) {
  hourEdits.value = new Map(hourEdits.value).set(cellKey, patch.hours);
  commentEdits.value = new Map(commentEdits.value).set(cellKey, patch.comment);
  if (patch.laborCode !== undefined) {
    laborCodeEdits.value = new Map(laborCodeEdits.value).set(cellKey, patch.laborCode);
  }
  clearRowError(cellKey);
}

function onRowRemove(cellKey: string) {
  removedCells.value = new Set(removedCells.value).add(cellKey);
  clearRowError(cellKey);
}

async function onRefreshAndRetry() {
  banner.value = null;
  await tsQuery.refetch();
  await onSubmit();
}

function buildPayload(): SaveTimesheetRequest {
  const entries: SaveTimesheetEntry[] = confirmedDrafts.value.map((d) => {
    // Pinia stores dayIndex as 0-based (0..14); the backend contract is 1..15.
    // No clamp: the draft engine now guarantees every dayIndex is in-period
    // (NUF-24), and the save validator still enforces [1,15] server-side.
    const dayIndexOneBased = (d.dayIndex ?? 0) + 1;
    return {
      wbs1: d.wbs1!,
      ...(d.wbs2 ? { wbs2: d.wbs2 } : {}),
      ...(d.wbs3 ? { wbs3: d.wbs3 } : {}),
      dayIndex: dayIndexOneBased,
      hours: d.hours ?? 0,
      ...(d.category ? { category: d.category } : {}),
      ...(d.comment ? { comment: d.comment } : {}),
      ...(d.laborCode ? { laborCode: d.laborCode } : {}),
      ...((cellSignatures.value.get(d.eventId)?.length ?? 0) > 0
        ? { signatureHashes: cellSignatures.value.get(d.eventId) }
        : {}),
      ...((cellEventIds.value.get(d.eventId)?.length ?? 0) > 0
        ? { eventIds: cellEventIds.value.get(d.eventId) }
        : {}),
    };
  });
  return {
    periodEnd: periodEnd.value,
    entries,
  };
}

async function onSubmit() {
  if (!canSubmit.value) return;
  banner.value = null;
  serverRowErrors.value = {};
  submitting.value = true;
  try {
    const payload = buildPayload();
    const writtenHours = payload.entries.reduce((s, e) => s + e.hours, 0);
    const refreshed = await saveTimesheet(payload);
    // Seed the timesheet cache with the freshly-written response so /home
    // re-renders immediately without a refetch round-trip. Keyed by the saved
    // period so the triage panel's period-scoped query reads it back.
    queryClient.setQueryData(['timesheet', periodEnd.value], refreshed);
    // In-page success confirmation. The redirect waits on the reconcile below,
    // so this is what the user reads while it runs.
    saved.value = { hours: writtenHours, lines: payload.entries.length };
    // Reconcile before leaving, not after arriving: the draft read is what marks
    // these hours as work Vantagepoint holds, and navigating ahead of it renders
    // the pre-save rows for a frame and offers them again (#6546). The active
    // period stays put, so the user lands back on the period they saved.
    reconciling.value = true;
    const reconciled = queryClient
      .refetchQueries({ queryKey: ['draft'], exact: false })
      .finally(() => { reconciling.value = false; });
    await Promise.all([
      reconciled,
      new Promise((resolve) => setTimeout(resolve, SUCCESS_DWELL_MS)),
    ]);
    void router.replace({ path: '/home', query: { saved: String(payload.entries.length) } });
  } catch (e) {
    handleSaveError(e);
    // The write can land even when its response is lost (timeout, 502). Re-read
    // VP before Publish is offered again, so anything written shows as already
    // in Vantagepoint instead of being sent twice (#7468).
    await tsQuery.refetch();
  } finally {
    submitting.value = false;
  }
}

// The pre-flight above catches these before they are sent, so reaching one
// means the client and the API disagree. Say what is wrong with the row anyway
// — a field name is not something to hand a user.
function fieldRejection(field: string): string {
  switch (field) {
    case 'dayIndex': return 'Outside this period — remove it to save the rest.';
    case 'wbs1': return 'No project on this entry — remove it to save the rest.';
    case 'hours': return 'Hours can’t be negative.';
    case 'category': return 'This entry’s category is blank.';
    default: return 'Vantagepoint won’t accept this entry — remove it to save the rest.';
  }
}

// eventId → VP's reason, for every row a rejection names. VP names a WBS level
// or a date only when it has one, so a blank one matches any.
function rowsRejectedBy(rejections: readonly VpRejection[]): Map<string, string> {
  const marked = new Map<string, string>();
  for (const r of rejections) {
    for (const d of confirmedDrafts.value) {
      if (d.wbs1 !== r.wbs1) continue;
      if (r.wbs2 && d.wbs2 !== r.wbs2) continue;
      if (r.wbs3 && d.wbs3 !== r.wbs3) continue;
      if (r.dates.length > 0 && !r.dates.includes(dayDate(d.dayIndex ?? 0))) continue;
      marked.set(d.eventId, r.message);
    }
  }
  return marked;
}

function dayDate(dayIndex: number): string {
  const [y, m, d] = periodStart.value.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + dayIndex)).toISOString().slice(0, 10);
}

function handleSaveError(e: unknown) {
  if (!(e instanceof ApiClientError)) {
    banner.value = { kind: 'error', message: e instanceof Error ? e.message : 'Publish failed; try again.' };
    return;
  }

  switch (e.code) {
    case 'bad_request': {
      // The shape validator answers in contract terms — "entries[0].dayIndex
      // must be 1..31." The reviewer had to decode that to find the row and
      // delete it by hand (#6236). Mark the row and say it in English; the
      // index is an array position they cannot see. Anything not in that shape
      // is already a sentence (the out-of-period rejection names its project),
      // so it passes through.
      const match = /entries\[(\d+)\]\.(\w+)/.exec(e.message);
      const target = match ? confirmedDrafts.value[Number(match[1])] : undefined;
      if (match && target) {
        serverRowErrors.value = { ...serverRowErrors.value, [target.eventId]: fieldRejection(match[2]) };
        banner.value = { kind: 'error', message: 'One entry can’t be saved. It’s marked below.' };
      } else {
        banner.value = { kind: 'error', message: e.message };
      }
      break;
    }
    case 'vp_unauthorized':
    case 'no_session':
      void router.replace({
        path: '/login',
        query: { flash: 'Session expired, please sign in again.' },
      });
      break;
    case 'period_locked':
      banner.value = {
        kind: 'error',
        message: 'This period is closed in Vantagepoint. Switch to an open period to add entries.',
      };
      break;
    case 'stale_master':
      // Covers VP's "state changed / period closed" rejection — show the
      // server's message (it names the closed-period possibility) with the
      // warn banner's Refresh-and-retry affordance.
      banner.value = { kind: 'warn', message: e.message };
      break;
    case 'vp_rejected': {
      // Put each of Vantagepoint's reasons on the rows it names, so the user can
      // see what to fix. A reason that matches nothing here keeps VP's own words.
      const marked = rowsRejectedBy(e.rejections);
      if (marked.size > 0) {
        serverRowErrors.value = { ...serverRowErrors.value, ...Object.fromEntries(marked) };
        banner.value = {
          kind: 'error',
          message: marked.size === 1
            ? 'One entry can’t be saved. It’s marked below.'
            : `${marked.size} entries can’t be saved. They’re marked below.`,
        };
      } else {
        banner.value = { kind: 'error', message: e.message };
      }
      break;
    }
    case 'vp_write_failed':
      banner.value = { kind: 'error', message: e.message };
      break;
    default:
      banner.value = { kind: 'error', message: `${e.message} (${e.code})` };
      break;
  }
}
</script>
